import React, { useMemo } from "react";
import {
  EuiBadge,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import {
  getAiSettings,
  getFeatures,
} from "src/helper/parser/opsanio";
import { useAnketaI18n } from "src/internationalization";
import { OpsanioApplianceAiBudgetMail } from "./ai-budget-mail";

/**
 * Which enabled features are worth naming, in the order they are shown.
 *
 * A deliberate whitelist rather than everything in OpsanioFeatures: webPush
 * carries keys, the OpenSearch tiers are topology rather than a capability, and
 * manageViaCapi says who owns the cluster lifecycle - none of them belong in a
 * "what does this appliance do" list.
 */
const FEATURE_LABELS: Array<{
  key:
    | "ai"
    | "siem"
    | "networkFlows"
    | "nuclei"
    | "openVas"
    | "compliance"
    | "certificatesManagement"
    | "guacamole"
    | "opsanioAr"
    | "opsanioAgent"
    | "bgp"
    | "developer"
    | "previewAccess";
  label: string;
}> = [
  { key: "ai", label: "AI / ML" },
  { key: "siem", label: "SIEM (Wazuh)" },
  { key: "networkFlows", label: "Network Flows" },
  { key: "nuclei", label: "Nuclei" },
  { key: "openVas", label: "OpenVAS" },
  { key: "compliance", label: "Compliance" },
  { key: "certificatesManagement", label: "Certificates" },
  { key: "guacamole", label: "Guacamole" },
  { key: "opsanioAr", label: "Active Response" },
  { key: "opsanioAgent", label: "opsanio Agent" },
  { key: "bgp", label: "BGP" },
  { key: "developer", label: "Developer Access" },
  { key: "previewAccess", label: "Preview Access" },
];

/**
 * The appliance's enabled features, read from the questionnaire facts.
 *
 * Exists because the AI/ML switch was previously visible only inside the
 * questionnaire's internal-only Features section: nothing in the dashboard said
 * whether a VPC was running a paid model gateway. AI is listed first and, when
 * on, carries its spend caps - an enabled gateway with no budget is the state
 * worth noticing, and the only place it was visible before was a values blob.
 *
 * Parsed here rather than server-side because the flyout already holds the raw
 * fact blob; `getFeatures` and `getAiSettings` are pure functions over it.
 */
export const OpsanioApplianceFlyoutFeatures = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element | null => {
  const { translate } = useAnketaI18n();

  const features = useMemo(() => {
    try {
      // The VAPID keys are irrelevant here and getFeatures wants them; empty
      // strings keep it out of the webPush branch entirely.
      return getFeatures(appliance.content, { private: "", public: "" });
    } catch {
      return null;
    }
  }, [appliance.content]);

  const ai = useMemo(() => {
    try {
      return getAiSettings(appliance.content);
    } catch {
      return null;
    }
  }, [appliance.content]);

  if (!features) return null;

  const enabled = FEATURE_LABELS.filter(({ key }) => features[key] === true);
  const euro = (value: number): string => `${value} €`;

  return (
    <>
      <EuiTitle size="xs">
        <h4>
          {translate("anketa.opsanio.appliance.flyout.features.title", {
            defaultMessage: "Enabled features",
          })}
        </h4>
      </EuiTitle>
      <EuiSpacer size="s" />

      {enabled.length === 0 ? (
        <EuiText size="s" color="subdued">
          {translate("anketa.opsanio.appliance.flyout.features.none", {
            defaultMessage: "No features enabled.",
          })}
        </EuiText>
      ) : (
        <EuiFlexGroup gutterSize="s" wrap responsive={false}>
          {enabled.map(({ key, label }) => (
            <EuiFlexItem grow={false} key={key}>
              {/* AI is the one that costs money per request, so it is the one
                  that gets a colour rather than blending into the row. */}
              <EuiBadge color={key === "ai" ? "accent" : "hollow"}>
                {label}
              </EuiBadge>
            </EuiFlexItem>
          ))}
        </EuiFlexGroup>
      )}

      {/* Everything below is AI-only: the budgets, the uncapped warning and the
          mail that reports them. On an appliance without the AI feature there is
          no gateway, no spend and nobody to inform. */}
      {features.ai && ai && (
        <>
          <EuiSpacer size="s" />
          {ai.budgets.enabled ? (
            <EuiText size="xs" color="subdued">
              {translate("anketa.opsanio.appliance.flyout.features.aiBudgets", {
                values: {
                  daily: euro(ai.budgets.daily.limit),
                  weekly: euro(ai.budgets.weekly.limit),
                  monthly: euro(ai.budgets.monthly.limit),
                },
                defaultMessage:
                  "AI spend budgets - daily {{daily}} · weekly {{weekly}} · monthly {{monthly}}",
              })}
            </EuiText>
          ) : (
            <EuiCallOut
              size="s"
              color="warning"
              iconType="alert"
              title={translate(
                "anketa.opsanio.appliance.flyout.features.aiNoBudget",
                {
                  defaultMessage:
                    "AI is enabled with spend budgets switched off - this gateway is uncapped.",
                },
              )}
            />
          )}
          <OpsanioApplianceAiBudgetMail
            appliance={appliance}
            recipients={ai.alerting?.email?.receiverEmails ?? []}
          />
        </>
      )}
    </>
  );
};
